import React, { useMemo } from "react"
import { FormProvider } from "react-hook-form"

import classNames from "classnames"

import { WithClassName } from "shared/types/WithClassName"
import { FormProvider as BaseFormProvider } from "shared/ui/form/FormContext"

import { ProjectRoleChip } from "entities/projects/ui/project-role-chip/ProjectRoleChip"
import { ProjectFormModel } from "features/project-management/types/ProjectFormModel"

import { ProjectFormProvider } from "./contexts/ProjectFormContext"
import { useProjectFormViewModel } from "./model/useProjectFormViewModel"
import "./styles/_project-form.scss"
import { ProjectFormControls } from "./ui/ProjectFormControls"
import { ProjectFormDescription } from "./ui/ProjectFormDescription"
import { ProjectFormDetailsButton } from "./ui/ProjectFormDetailsButton"
import { ProjectFormInflationSurcharge } from "./ui/ProjectFormInflationSurcharge"
import { ProjectFormLastChanged } from "./ui/ProjectFormLastChanged"
import { ProjectFormLastChangedBy } from "./ui/ProjectFormLastChangedBy"
import { ProjectFormPhase } from "./ui/ProjectFormPhase"
import { ProjectFormPriceGroups } from "./ui/ProjectFormPriceGroups"
import { ProjectFormPriceList } from "./ui/ProjectFormPriceList"
import { ProjectFormTabs } from "./ui/ProjectFormTabs"
import { ProjectFormTeamButton } from "./ui/ProjectFormTeamButton"
import { ProjectFormTitle } from "./ui/ProjectFormTitle"

export interface ProjectFormProps extends ProjectFormModel, WithClassName {
  id?: string
  isReadonly?: boolean
  isDisabled?: boolean
  children?: React.ReactNode
}

const ProjectFormRoot = React.memo((props: ProjectFormProps) => {
  const {
    id,
    className,
    children,
    isReadonly,
    isDisabled,
    isLoading,
    onCancel,
    model
  } = props

  const { form, onSubmit } = useProjectFormViewModel(props)

  const contextValue = useMemo(
    () => ({
      isReadonly,
      isDisabled,
      isLoading,
      onCancel,
      model
    }),
    [isReadonly, isDisabled, isLoading, onCancel, model]
  )

  return (
    <BaseFormProvider isReadonly={isReadonly} isDisabled={isDisabled}>
      <ProjectFormProvider value={contextValue}>
        <FormProvider {...form}>
          <form
            id={id}
            className={classNames("project-form", className)}
            onSubmit={onSubmit}
          >
            {children}
          </form>
        </FormProvider>
      </ProjectFormProvider>
    </BaseFormProvider>
  )
})

ProjectFormRoot.displayName = "ProjectForm"

export const ProjectForm = Object.assign(ProjectFormRoot, {
  Title: ProjectFormTitle,
  Description: ProjectFormDescription,
  InflationSurcharge: ProjectFormInflationSurcharge,
  PriceList: ProjectFormPriceList,
  Phase: ProjectFormPhase,
  RoleChip: ProjectRoleChip,
  LastChanged: ProjectFormLastChanged,
  LastChangedBy: ProjectFormLastChangedBy,
  PriceGroups: ProjectFormPriceGroups,
  FormControls: ProjectFormControls,
  TeamButton: ProjectFormTeamButton,
  DetailsButton: ProjectFormDetailsButton,
  Tabs: ProjectFormTabs
})
